/* time.js
 * DST-safe local time utilities for sports cards.
 * - Uses the GAME DATE, not "today", so DST rules are correct.
 * - Accepts columns such as GameTime and GameDate.
 * - Falls back to common date/time aliases.
 * - Supports KBO source times in Asia/Seoul.
 *
 * Usage:
 *   import { formatLocalTime, formatTipoff } from "./time";
 *
 *   const label = formatLocalTime(card, "KBO");
 *   const line = formatTipoff(card, "Away", "KBO");
 */

// League default time zones.
// These are also used as a fallback when no source time zone is available.
const LEAGUE_TZ = {
  NBA: "America/New_York",
  NHL: "America/New_York",
  MLB: "America/New_York",
  KBO: "Asia/Seoul",
  NFL: "America/New_York",
  WNBA: "America/New_York",
  CFB: "America/New_York",
  CBB: "America/New_York",
  MMA: "America/New_York",
  UFC: "America/New_York",
  SOCCER: "UTC",
};

// The time zone in which each sport's incoming GameTime is expressed.
const SOURCE_TZ = {
  NBA: "America/Chicago",
  NHL: "America/New_York",
  MLB: "America/New_York",
  KBO: "Asia/Seoul",
  NFL: "America/New_York",
  WNBA: "America/New_York",
  CFB: "America/New_York",
  CBB: "America/New_York",
  MMA: "America/New_York",
  UFC: "America/New_York",
  SOCCER: "UTC",
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const pad2 = (value) => String(value).padStart(2, "0");

function firstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
}

/**
 * Normalize Excel-style and ISO-style date values into YYYY-MM-DD.
 */
function sanitizeDate(raw) {
  if (!raw) {
    return null;
  }

  const value = String(raw).trim();

  // YYYY-MM-DD HH:MM:SS
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(value)) {
    return value.split(" ")[0];
  }

  // ISO date/time, such as 2026-07-26T00:00:00.
  const isoMatch = value.match(/\d{4}-\d{2}-\d{2}/);

  if (isoMatch) {
    return isoMatch[0];
  }

  // Excel date serial.
  if (!Number.isNaN(Number(raw)) && Number(raw) > 40000) {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(
      excelEpoch.getTime() + Number(raw) * 86400000
    );

    return date.toISOString().slice(0, 10);
  }

  return value;
}

/**
 * Parse a wall-clock time from strings such as:
 *
 * 6:00 PM
 * 18:00
 * 6 PM
 * 6:00 PM KST
 * 6:00 PM — Away
 */
function parseWallClock(timeString) {
  if (!timeString) {
    return null;
  }

  const raw = String(timeString).trim();

  // h:mm AM/PM or h AM/PM.
  let match = raw.match(
    /(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i
  );

  if (match) {
    let hour = Number.parseInt(match[1], 10);
    const minute = match[2]
      ? Number.parseInt(match[2], 10)
      : 0;

    const meridian = match[3].toLowerCase();

    if (meridian === "pm" && hour !== 12) {
      hour += 12;
    }

    if (meridian === "am" && hour === 12) {
      hour = 0;
    }

    return { hour, minute };
  }

  // 24-hour HH:MM.
  match = raw.match(/\b(\d{1,2}):(\d{2})\b/);

  if (match) {
    return {
      hour: Number.parseInt(match[1], 10),
      minute: Number.parseInt(match[2], 10),
    };
  }

  return null;
}

/**
 * Determine the UTC offset, in minutes, for an IANA time zone at a given time.
 */
function getTimeZoneOffsetMinutes(timeZone, utcDate) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = formatter.formatToParts(utcDate);
  const values = {};

  for (const part of parts) {
    values[part.type] = part.value;
  }

  const representedAsUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return (representedAsUtc - utcDate.getTime()) / 60000;
}

/**
 * Convert a wall-clock date and time in an IANA time zone into a UTC Date.
 */
function wallTimeToUTC(
  { year, month, day, hour, minute },
  timeZone
) {
  const initialUtcGuess = Date.UTC(
    year,
    month - 1,
    day,
    hour,
    minute,
    0,
    0
  );

  const offsetMinutes = getTimeZoneOffsetMinutes(
    timeZone,
    new Date(initialUtcGuess)
  );

  return new Date(
    initialUtcGuess - offsetMinutes * 60000
  );
}

/**
 * Extract the game date from supported card fields.
 */
function extractGameDate(card) {
  const raw = firstDefined(
    card?.GameDate,
    card?.Date,
    card?.game_date,
    card?.Game_Date,
    card?.["Game Date"],
    card?.["Date Only"],
    card?.Slate,
    card?.date
  );

  if (!raw) {
    return null;
  }

  const cleanDate = sanitizeDate(raw);

  const match = String(cleanDate).match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (match) {
    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };
  }

  const parsed = new Date(cleanDate);

  if (!Number.isNaN(parsed.getTime())) {
    return {
      year: parsed.getFullYear(),
      month: parsed.getMonth() + 1,
      day: parsed.getDate(),
    };
  }

  return null;
}

/**
 * Extract the game time from supported card fields.
 */
function extractGameTime(card) {
  const raw = firstDefined(
    card?.GameTime,
    card?.Time,
    card?.time,
    card?.Tipoff,
    card?.StartTime,
    card?.Start_Time,
    card?.["Game Time"],
    card?.["Start Time"]
  );

  return raw ? String(raw).trim() : undefined;
}

function extractAbsoluteStart(card) {
  const raw = firstDefined(
    card?.["Game Start Timestamp"],
    card?.GameStartTimestamp,
    card?.game_start_timestamp,
    card?.start_timestamp,
    card?.["Start Time"],
    card?.StartTime
  );
  if (!raw) return null;
  const value = String(raw).trim();
  if (!/^\d{4}-\d{2}-\d{2}T/.test(value) || !/(?:Z|[+-]\d{2}:?\d{2})$/.test(value)) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveGameStart(card, sportKey, options = {}) {
  const absoluteStart = extractAbsoluteStart(card);
  if (absoluteStart) return absoluteStart;

  const gameDate = extractGameDate(card);
  const wallClock = parseWallClock(extractGameTime(card));
  if (!gameDate || !wallClock) return null;

  const sourceZone =
    firstDefined(
      options?.sourceTz,
      options?.sourceTZ,
      card?.SourceTz,
      card?.SourceTZ,
      card?.SourceTimeZone,
      card?.Timezone,
      card?.TimeZone,
      SOURCE_TZ[sportKey],
      LEAGUE_TZ[sportKey]
    ) || "America/New_York";

  try {
    return wallTimeToUTC(
      { ...gameDate, hour: wallClock.hour, minute: wallClock.minute },
      sourceZone
    );
  } catch (error) {
    console.warn("Unable to convert game time:", { sport: sportKey, sourceZone, gameDate }, error);
    return null;
  }
}

/**
 * Normalize the sport key used for time-zone lookup.
 */
function normalizeSport(sport) {
  return String(sport || "NBA")
    .trim()
    .toUpperCase();
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Convert a card's source game time into the viewer's local time.
 *
 * KBO example:
 *   GameDate: "2026-07-27"
 *   GameTime: "6:00 PM"
 *   source zone: Asia/Seoul
 *
 * A user in Houston will see the corresponding Houston time.
 */
export function formatLocalTime(
  card,
  sport = "NBA",
  options = {}
) {
  const sportKey = normalizeSport(sport);
  const absoluteStart = extractAbsoluteStart(card);
  const utcDate = resolveGameStart(card, sportKey, options);
  if (!utcDate) return "TBD";

  const formatted = utcDate.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
  });
  return absoluteStart ? formatted : formatted.toLowerCase();
}

/** Return the game weekday in the same viewer timezone used for the card time. */
export function formatGameWeekday(card, sport = "NBA", options = {}) {
  const start = resolveGameStart(card, normalizeSport(sport), options);
  if (!start) return "";
  const weekday = start.toLocaleDateString("en-US", {
    weekday: "short",
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
  });
  return /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)$/.test(weekday) ? weekday : "";
}

/** Format an absolute event timestamp in the viewer's browser timezone. */
export function formatLocalEventTime(card, sport = "NBA", options = {}) {
  const absoluteStart = extractAbsoluteStart(card);
  if (!absoluteStart) return formatLocalTime(card, sport, options);
  return absoluteStart.toLocaleString(options.locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    ...options.format,
  }).replace(",", " •");
}

export function oldestFirstWindowForDisplay(rows, limit = 5) {
  return (Array.isArray(rows) ? rows.slice(0, limit) : []).reverse();
}

/**
 * Return a compact game-date label such as 07/27.
 */
export function formatGameDate(card) {
  const gameDate = extractGameDate(card);

  if (!gameDate) {
    return "";
  }

  return `${pad2(gameDate.month)}/${pad2(gameDate.day)}`;
}

/**
 * Return a formatted time plus Home/Away label.
 *
 * Example:
 *   4:00 am — ✈️ Away
 */
export function formatTipoff(
  card,
  homeAway,
  sport = "NBA"
) {
  const formattedTime = formatLocalTime(card, sport);

  if (!homeAway) {
    return formattedTime;
  }

  const icon = /away/i.test(String(homeAway))
    ? "✈️"
    : "🏠";

  return `${formattedTime} — ${icon} ${homeAway}`;
}
